import { BundleTag } from '@inkitt/galatea-common/types/payment-backend/entities/payment-bundle';

import { PointsBundle } from '../../types/PointsBundle';

/**
 * Points packages for Audiobooks Plus (the audio-only Galatea clone, internal slug `cashcow`).
 *
 * Its IAP products live on its own App Store / Play listing, so it cannot sell Galatea's
 * `com.colt.app.*` products — the ids below are the ones registered for this app. They mirror the
 * bundles already described in the client's `staticRemoteConfig.json`, which drives the legacy v1
 * purchase path; this module is the v2 (`getPointsMap`) counterpart.
 *
 * The brand has no subscriptions and no pricing tests, so there is a single `default` group whose
 * key matches the brand's RevenueCat offering (`BrandCapabilities.revenueCat.defaultOffering`) —
 * offerings are looked up by key, so the two must stay in sync.
 *
 * DEV-6331 reshaped the ladder to six Pocket-FM-style packages sold as "base points + free points":
 *
 * | package  | base | free | credited | bonus | US price |
 * |----------|------|------|----------|-------|----------|
 * | small    |   12 |   12 |       24 |  100% |    $1.99 |
 * | medium   |   34 |   42 |       76 |  120% |    $4.99 |
 * | big      |   68 |  102 |      170 |  150% |    $9.99 |
 * | bigger   |  110 |  165 |      275 |  150% |   $15.99 |
 * | biggest  |  136 |  272 |      408 |  200% |   $19.99 |
 * | ultimate |  204 |  408 |      612 |  200% |   $29.99 |
 *
 * A seventh product, the trial pack, is defined below but is deliberately NOT part of that ladder —
 * see its own doc comment.
 *
 * The free/paid split is presentation only — nothing downstream distinguishes the two kinds of
 * coins, so `tiers` (what actually gets credited) is the full base + free total and `bonusPoints`
 * carries the free part purely so the card can print it. Prices are not in here at all; they come
 * from the store via RevenueCat and are listed above only to pin which product is which.
 */
export const audiobooksPlusPointsSmallPack: PointsBundle = {
  id: 'com.inkitt.galatea.audiobooks.points.small',
  adjustEventId: '78698z',
  tiers: 24,
  bonusPoints: 12,
  bonusPercent: 100,
  title: '24 Points',
  // No chapter copy: audiobook chapter prices are dynamic, so a fixed "N chapters" line would be
  // wrong for most series. The card renders nothing when this is empty.
  description: '',
  tags: [BundleTag.Default],
};

export const audiobooksPlusPointsMediumPack: PointsBundle = {
  id: 'com.inkitt.galatea.audiobooks.points.medium',
  adjustEventId: '9dwhhe',
  tiers: 76,
  bonusPoints: 42,
  bonusPercent: 120,
  title: '76 Points',
  description: '',
  tags: [BundleTag.Default],
};

export const audiobooksPlusPointsBigPack: PointsBundle = {
  id: 'com.inkitt.galatea.audiobooks.points.big',
  adjustEventId: 'euwwc4',
  tiers: 170,
  bonusPoints: 102,
  bonusPercent: 150,
  title: '170 Points',
  description: '',
  tags: [BundleTag.Default],
};

export const audiobooksPlusPointsBiggerPack: PointsBundle = {
  id: 'com.inkitt.galatea.audiobooks.points.bigger',
  adjustEventId: 'ze3zx9',
  tiers: 275,
  bonusPoints: 165,
  bonusPercent: 150,
  title: '275 Points',
  description: '',
  isMostPopular: true,
  tags: [BundleTag.Default],
};

/**
 * The one product added for DEV-6331 — the old five-package ladder had no slot between `bigger` and
 * `ultimate`. Its Adjust event id is still the shared one; ask marketing for a dedicated token if
 * per-package attribution is wanted.
 */
export const audiobooksPlusPointsBiggestPack: PointsBundle = {
  id: 'com.inkitt.galatea.audiobooks.points.biggest',
  adjustEventId: '',
  tiers: 408,
  bonusPoints: 272,
  bonusPercent: 200,
  title: '408 Points',
  description: '',
  tags: [BundleTag.Default],
};

export const audiobooksPlusPointsUltimatePack: PointsBundle = {
  id: 'com.inkitt.galatea.audiobooks.points.ultimate',
  adjustEventId: 'hxgn1u',
  tiers: 612,
  bonusPoints: 408,
  bonusPercent: 200,
  title: '612 Points',
  description: '',
  tags: [BundleTag.Default],
};

/**
 * The RevenueCat offering — and the matching group below — that holds nothing but the trial pack.
 *
 * This separation is what actually keeps the pack away from users who must not see it, and it works
 * on app versions that predate the whole feature: a client only ever resolves products from the one
 * offering key it asks for, and every build shipped so far asks for `default`. A pack sitting in
 * `default` would instead be rendered as an ordinary ladder row by any build without the gate,
 * whatever the AB test says — at $0.99 for 340 points, next to $19.99 for 408.
 *
 * The client resolves this key as a constant rather than from the AB payload, so both arms compute
 * the same audience for exposure without depending on how the flag is configured.
 */
export const AUDIOBOOKS_PLUS_TRIAL_OFFERING_KEY = 'audiobooks_trial_pack';

/**
 * Trial pack — 340 points for $0.99, shown once to users who have never bought anything
 * (Figma "Buy Points", node 116:1041). Sold as "136 Points + 204 Free points", 150% bonus.
 *
 * Deliberately NOT part of `audiobooksPlusAllPointsBundles`: it lives in its own offering (see
 * above). `isTrialPack` is a second, belt-and-braces guard — `AudiobooksPointsTab` keeps anything
 * carrying it out of the ladder, so the pack stays hidden even if it is one day added to `default`
 * by mistake.
 *
 * Who sees the card is decided in the client: the `testAudiobooksTrialPack` arm plus "no purchases
 * yet". The one-purchase-per-account limit is enforced in jupiter, not here.
 *
 * `tiers` must match what jupiter credits for this SKU on /v6/payment/buy — the client only prints
 * the number, it never decides it.
 */
export const audiobooksPlusPointsTrialPack: PointsBundle = {
  id: 'com.inkitt.galatea.audiobooks.points.trialpack',
  // TODO(litly): dedicated Adjust token — first-purchase attribution collapses into the shared
  // event without it. Ask marketing, same open question as `audiobooksPlusPointsBiggestPack`.
  adjustEventId: '',
  tiers: 340,
  bonusPoints: 204,
  bonusPercent: 150,
  title: '340 Points',
  description: '',
  isTrialPack: true,
  tags: [BundleTag.Default],
};

export const audiobooksPlusAllPointsBundles: PointsBundle[] = [
  audiobooksPlusPointsSmallPack,
  audiobooksPlusPointsMediumPack,
  audiobooksPlusPointsBigPack,
  audiobooksPlusPointsBiggerPack,
  audiobooksPlusPointsBiggestPack,
  audiobooksPlusPointsUltimatePack,
];

/** Contents of the trial-pack offering: exactly one pack, by design. */
export const audiobooksPlusTrialPackBundles: PointsBundle[] = [audiobooksPlusPointsTrialPack];
